import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

// Public Components
import LandingPage from "./pages/public/LandingPage";
import AssessmentPage from "./pages/public/AssessmentPage";
import ResultsPage from "./pages/public/ResultsPage";
import BookingPage from "./pages/public/BookingPage";
import DocumentUploadPage from "./pages/public/DocumentUploadPage";
import Header from "./components/common/Header";

// Admin Components
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import QuestionManager from "./pages/admin/QuestionManager";
import StudentManager from "./pages/admin/StudentManager";
import AppointmentManager from "./pages/admin/AppointmentManager";
import DocumentReview from "./pages/admin/DocumentReview";
import UserManagement from "./pages/admin/UserManagement";
import UserSettings from "./pages/admin/UserSettings";
import AdminSearchResults from "./pages/admin/AdminSearchResults";

// Common Components
import Footer from "./components/common/Footer";
import AdminLayout from "./components/admin/AdminLayout";

// Auth Context
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import StudentDetail from "./pages/admin/StudentDetail";
import AppointmentDetail from "./pages/admin/AppointmentDetail";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin } = useAuth();

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/admin/login" />;
  }

  if (requireAdmin && !isAuthenticated) {
    return <Navigate to="/admin/login" />;
  }

  return children;
};

// Public Layout Component
const PublicLayout = ({ children }) => {
  return (
    <>
      <Header />
      {children}
      <Footer />
    </>
  );
};

// Admin Layout Wrapper
const AdminLayoutWrapper = ({ children }) => {
  return (
    <ProtectedRoute requireAdmin={true}>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes with Header & Footer */}
            <Route
              path="/"
              element={
                <PublicLayout>
                  <LandingPage />
                </PublicLayout>
              }
            />

            <Route
              path="/assessment"
              element={
                <PublicLayout>
                  <AssessmentPage />
                </PublicLayout>
              }
            />

            <Route
              path="/results"
              element={
                <PublicLayout>
                  <ResultsPage />
                </PublicLayout>
              }
            />

            <Route
              path="/booking"
              element={
                <PublicLayout>
                  <BookingPage />
                </PublicLayout>
              }
            />

            <Route
              path="/documents/:appointmentId"
              element={
                <PublicLayout>
                  <DocumentUploadPage />
                </PublicLayout>
              }
            />

            {/* Admin Login (No Layout) */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Admin Routes with AdminLayout (No public Header/Footer) */}
            <Route
              path="/admin"
              element={
                <AdminLayoutWrapper>
                  <AdminDashboard />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/users"
              element={
                <AdminLayoutWrapper>
                  <UserManagement />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/settings"
              element={
                <AdminLayoutWrapper>
                  <UserSettings />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/questions"
              element={
                <AdminLayoutWrapper>
                  <QuestionManager />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/students"
              element={
                <AdminLayoutWrapper>
                  <StudentManager />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/students/:id"
              element={
                <AdminLayoutWrapper>
                  <StudentDetail />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/appointments"
              element={
                <AdminLayoutWrapper>
                  <AppointmentManager />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/appointments/:appointmentId"
              element={
                <AdminLayoutWrapper>
                  <AppointmentDetail />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/documents"
              element={
                <AdminLayoutWrapper>
                  <DocumentReview />
                </AdminLayoutWrapper>
              }
            />

            <Route
              path="/admin/search"
              element={
                <AdminLayoutWrapper>
                  <AdminSearchResults />
                </AdminLayoutWrapper>
              }
            />

            {/* Catch-all route - redirect to home */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
